import type { Rng } from './rng.js';

export type GameType = 'CALL_BREAK' | 'SPADES' | 'TARNEEB' | 'HEARTS' | 'FIVE_HUNDRED' | 'JUDGEMENT' | 'RUMMY' | 'EUCHRE' | 'GIN_RUMMY' | 'TWENTY_NINE' | 'CRAZY_EIGHTS' | 'CRIBBAGE' | 'BLACKJACK' | 'BHABHI' | 'PINOCHLE' | 'ANDAR_BAHAR' | 'BRIDGE' | 'SEVENS' | 'SCOPA' | 'BACCARAT' | 'BLUFF' | 'DRAGON_TIGER' | 'FLUSH' | 'COURT_PIECE' | 'DEHLA_PAKAD' | 'WHIST' | 'SEEP' | 'BELOTE' | 'DURAK' | 'PRESIDENT' | 'BRISCOLA' | 'CASINO_WAR' | 'THREE_CARD_POKER' | 'GO_FISH' | 'TRESSETTE' | 'RED_DOG' | 'CANASTA' | 'SKAT' | 'PAI_GOW' | 'BIG_TWO' | 'WAR' | 'RUMMY_500' | 'BRAG' | 'PITCH' | 'HAZARI' | 'MARRIAGE' | 'TEEN_PATTI' | 'POKER';
export const GAME_TYPES: readonly GameType[] = ['CALL_BREAK', 'SPADES', 'TARNEEB', 'HEARTS', 'FIVE_HUNDRED', 'JUDGEMENT', 'RUMMY', 'EUCHRE', 'GIN_RUMMY', 'TWENTY_NINE', 'CRAZY_EIGHTS', 'CRIBBAGE', 'BLACKJACK', 'BHABHI', 'PINOCHLE', 'ANDAR_BAHAR', 'BRIDGE', 'SEVENS', 'SCOPA', 'BACCARAT', 'BLUFF', 'DRAGON_TIGER', 'FLUSH', 'COURT_PIECE', 'DEHLA_PAKAD', 'WHIST', 'SEEP', 'BELOTE', 'DURAK', 'PRESIDENT', 'BRISCOLA', 'CASINO_WAR', 'THREE_CARD_POKER', 'GO_FISH', 'TRESSETTE', 'RED_DOG', 'CANASTA', 'SKAT', 'PAI_GOW', 'BIG_TWO', 'WAR', 'RUMMY_500', 'BRAG', 'PITCH', 'HAZARI', 'MARRIAGE', 'TEEN_PATTI', 'POKER'];

/**
 * - `match`   – fixed-length competition with an entry fee and placement payouts (Call Break).
 * - `session` – chip-based play; players buy in, chips move hand by hand, players cash out on leave
 *               (Marriage, Teen Patti, Poker cash tables).
 */
export type EngineKind = 'match' | 'session';

export type EngineErrorCode =
  | 'NOT_YOUR_TURN'
  | 'INVALID_PHASE'
  | 'INVALID_ACTION'
  | 'INVALID_CARD'
  | 'ILLEGAL_MOVE'
  | 'INVALID_AMOUNT'
  | 'INSUFFICIENT_CHIPS'
  | 'INVALID_CONFIG'
  | 'SEAT_TAKEN'
  | 'GAME_OVER';

export class EngineError extends Error {
  constructor(
    readonly code: EngineErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'EngineError';
  }
}

export function assertEngine(cond: unknown, code: EngineErrorCode, message: string): asserts cond {
  if (!cond) throw new EngineError(code, message);
}

export interface SeatSetup {
  seat: number;
  playerId: string;
  /** Starting chips for session games (escrowed buy-in). Ignored by match games. */
  chips?: number;
}

/** Public, animation-friendly event. MUST NOT contain information hidden from any player. */
export interface EngineEvent {
  type: string;
  seat?: number;
  [key: string]: unknown;
}

export interface Standing {
  seat: number;
  /** 1-based finishing place; tied players share a place. */
  place: number;
  /** Game-specific final score (Call Break: tenths of a point). */
  score: number;
}

/** A step the platform must run without player input (e.g. next round after the results pause). */
export interface SystemStep {
  kind: string;
  delayMs: number;
}

/**
 * Contract implemented by every game engine.
 *
 * Engines are synchronous and deterministic for a given RNG. They mutate the state object they are
 * given ONLY after an action has been fully validated, so a thrown {@link EngineError} never leaves
 * a half-applied action behind. All randomness comes from the injected {@link Rng}.
 *
 * @typeParam C config, S state, A action, V per-seat view, L legal-action description
 */
export interface GameEngine<C, S, A, V, L> {
  readonly gameType: GameType;
  readonly kind: EngineKind;
  defaultConfig(): C;
  /** Merges partial config onto defaults and validates it. Throws INVALID_CONFIG. */
  normalizeConfig(input?: Partial<C>): C;
  seatLimits(config: C): { min: number; max: number };
  createState(config: C, seats: SeatSetup[], rng: Rng): S;
  /** Validates untrusted action payloads (shape only; legality is checked by applyAction). */
  parseAction(raw: unknown): A;
  applyAction(state: S, seat: number, action: A, rng: Rng): EngineEvent[];
  /** Seats whose input the engine is currently waiting for. */
  getActors(state: S): number[];
  getLegalActions(state: S, seat: number): L;
  /** Safe automatic action for a seat whose turn timer expired. */
  getTimeoutAction(state: S, seat: number): A;
  /** Redacted view for a seat (null = spectator). Never leaks other players' hidden cards. */
  getView(state: S, seat: number | null): V;
  getSystemStep(state: S): SystemStep | null;
  runSystemStep(state: S, rng: Rng): EngineEvent[];
  isFinished(state: S): boolean;
  getStandings(state: S): Standing[];
  /** Session games: current chip stacks by seat. */
  getChips?(state: S): Record<number, number>;
  /** Session games: seat a new player between hands. */
  addSeat?(state: S, setup: SeatSetup): EngineEvent[];
  /** Session games: remove a player (folds them if a hand is running). Returns chips to cash out. */
  removeSeat?(state: S, seat: number): { chips: number; events: EngineEvent[] };
  /** Session games: true while a hand/round is in progress. */
  isHandInProgress?(state: S): boolean;
  /** Rounds/hands completed so far (used for missions such as "complete 5 rounds"). */
  getRoundsCompleted(state: S): number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyGameEngine = GameEngine<any, any, any, any, any>;

export function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function standingsFromScores(scores: readonly { seat: number; score: number }[]): Standing[] {
  const sorted = [...scores].sort((a, b) => b.score - a.score);
  const out: Standing[] = [];
  sorted.forEach((s, i) => {
    const prev = out[i - 1];
    const place = prev && prev.score === s.score ? prev.place : i + 1;
    out.push({ seat: s.seat, score: s.score, place });
  });
  return out;
}
